import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGabarits, fetchGabarit, setSelectedGabarit } from "state/gabarit/actions";
import {
  resetOrderEdition,
  setOrderEditionInfo,
} from "state/orderEdition/actions";
import { resetTenderCatalog } from "state/tender/actions";
import { initialize } from "state/adminData/actions";
import GabaritItem from "./GabaritItem";
import { Button, Form } from "react-bootstrap";
import { faFileAlt, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { css } from "emotion";
import { toast } from "react-toastify";
import swal from "sweetalert";

const nouvelIdBrique = () => `devis_brique_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

/**
 * Applique un gabarit à un devis DÉJÀ enregistré (typiquement créé depuis le pipeline) sans
 * en perdre l'identité : numéro de devis, client, entreprise, facturation, adresse de
 * livraison, chargé de clientèle et date/couverts/contenant de chaque prestation sont
 * conservés ; le contenu des prestations (format, articles, briques…) vient du gabarit.
 * Les prestations du gabarit se posent dans l'ordre sur celles du devis ; celles en plus
 * sont ajoutées, celles du devis sans équivalent sont gardées telles quelles.
 * Avant, le gabarit remettait tout l'éditeur à zéro : la sauvegarde créait un second devis,
 * sans client ni lien avec la fiche événement (retour terrain Keep, 26/10/2026).
 */
export const appliquerGabaritSurDevis = (orderEdition, gabaritData) => {
  const existants = orderEdition.orders || [];
  const modele = existants[0] || {};
  const ordersGabarit = (gabaritData.orders || []).map((orderGabarit, i) => {
    const existant = existants[i];
    const base = existant || modele;
    const merged = {
      ...orderGabarit,
      id: existant ? existant.id : null,
      status: existant ? existant.status : "101",
      info: existant ? existant.info : {},
      // Date, couverts, horaires et adresse : ceux du devis (le gabarit n'en sait rien)
      deliveryInfo: { ...(orderGabarit.deliveryInfo || {}), ...(base.deliveryInfo || {}) },
      // Contenant choisi sur la demande (ex. zéro déchet) prioritaire sur celui du gabarit
      packagingType: base.packagingType || orderGabarit.packagingType || null,
      service: base.service ?? orderGabarit.service,
    };
    if (Array.isArray(merged.briqueDevis)) {
      // Identifiants de briques neufs : partagés, ils mélangent le contenu des prestations (6.63)
      merged.briqueDevis = merged.briqueDevis.map((b) => ({ ...b, _devisId: nouvelIdBrique() }));
    }
    return merged;
  });
  const orders = [...ordersGabarit, ...existants.slice(ordersGabarit.length)];
  return {
    orders,
    // Champs de devis repris du gabarit seulement s'ils sont vides sur le devis
    customerSpecialCommentary: orderEdition.customerSpecialCommentary || gabaritData.customerSpecialCommentary || null,
    customerQuotationCommentary: orderEdition.customerQuotationCommentary || gabaritData.customerQuotationCommentary || null,
  };
};

const GabaritSelector = () => {
  const dispatch = useDispatch();
  const gabaritState = useSelector((state) => state.gabarit);
  const { list, selected, loading, isInitialized } = gabaritState;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const popoverRef = useRef(null);

  // Charge les gabarits au premier rendu
  useEffect(() => {
    if (!isInitialized && !loading) {
      dispatch(fetchGabarits());
    }
  }, [dispatch, isInitialized, loading]);

  // Ferme le popover en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredList = list.filter((g) =>
    g.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelectDevisVierge = async () => {
    if (orderEdition?.groupedOrderId) {
      const confirm = await swal({
        title: "Partir d'un devis vierge ?",
        text: "Ce devis est déjà enregistré : « Devis vierge » vide l'éditeur et la prochaine sauvegarde créera un NOUVEAU devis, sans lien avec celui-ci.",
        icon: "warning",
        buttons: ["Annuler", "Nouveau devis vierge"],
        dangerMode: true,
      });
      if (!confirm) return;
    }
    dispatch(resetOrderEdition());
    dispatch(resetTenderCatalog());
    dispatch(initialize());
    dispatch(setSelectedGabarit(null));
    setIsOpen(false);
    toast.info("Formulaire réinitialisé — Devis vierge");
  };

  const orderEdition = useSelector((state) => state.orderEdition);

  const handleSelectGabarit = async (gabarit) => {
    setIsOpen(false);
    // Devis déjà enregistré : on remplit ses prestations au lieu de repartir d'un devis neuf.
    if (orderEdition?.groupedOrderId) {
      const confirm = await swal({
        title: `Appliquer le gabarit « ${gabarit.name} » ?`,
        text: "Le contenu des prestations est remplacé par celui du gabarit. Le numéro de devis, le client, les adresses, les dates, les couverts et le contenant sont conservés. Rien n'est enregistré tant que vous ne sauvegardez pas.",
        icon: "info",
        buttons: ["Annuler", "Appliquer"],
      });
      if (!confirm) return;
      try {
        const fullGabarit = await dispatch(fetchGabarit(gabarit.id));
        if (!fullGabarit?.data) {
          toast.error("Gabarit vide ou introuvable");
          return;
        }
        dispatch(setOrderEditionInfo(appliquerGabaritSurDevis(orderEdition, fullGabarit.data)));
        dispatch(setSelectedGabarit(fullGabarit));
        toast.success(`Gabarit "${gabarit.name}" appliqué — pensez à sauvegarder`);
      } catch (err) {
        toast.error("Erreur lors du chargement du gabarit");
      }
      return;
    }
    try {
      // Fetch le gabarit complet avec ses data
      const fullGabarit = await dispatch(fetchGabarit(gabarit.id));
      dispatch(resetOrderEdition());
      dispatch(resetTenderCatalog());
      dispatch(initialize());
      setTimeout(() => {
        if (fullGabarit && fullGabarit.data) {
          dispatch(setOrderEditionInfo(fullGabarit.data));
        }
        dispatch(setSelectedGabarit(fullGabarit || gabarit));
        toast.success(`Gabarit "${gabarit.name}" chargé`);
      }, 100);
    } catch (err) {
      toast.error("Erreur lors du chargement du gabarit");
    }
  };

  return (
    <div className={css(styles.container)} ref={popoverRef}>
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={css(styles.triggerBtn)}
      >
        <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: "6px" }} />
        {selected ? selected.name : "Choisir un gabarit"}
        <FontAwesomeIcon
          icon={faChevronDown}
          style={{
            marginLeft: "8px",
            transition: "transform 0.2s",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Button>

      {isOpen && (
        <div className={css(styles.popover)}>
          <div className={css(styles.searchWrapper)}>
            <Form.Control
              type="text"
              placeholder="Rechercher un gabarit..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="sm"
              autoFocus
            />
          </div>

          <div className={css(styles.list)}>
            {/* Devis vierge — toujours en premier */}
            <div
              className={css(
                styles.devisVierge,
                !selected && styles.devisViergeSelected
              )}
              onClick={handleSelectDevisVierge}
            >
              <FontAwesomeIcon
                icon={faFileAlt}
                style={{ marginRight: "8px", color: "#579862" }}
              />
              Devis vierge
            </div>

            {loading && (
              <div className={css(styles.loading)}>Chargement...</div>
            )}

            {!loading && filteredList.length === 0 && (
              <div className={css(styles.empty)}>Aucun gabarit trouvé</div>
            )}

            {filteredList.map((gabarit) => (
              <GabaritItem
                key={gabarit.id}
                gabarit={gabarit}
                onSelect={handleSelectGabarit}
                isSelected={selected?.id === gabarit.id}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    position: "relative",
    display: "inline-block",
  },
  triggerBtn: {
    borderColor: "#579862",
    color: "#579862",
    ":hover": {
      backgroundColor: "#579862",
      color: "#fff",
    },
  },
  popover: {
    position: "absolute",
    top: "100%",
    right: 0,
    zIndex: 1050,
    width: "320px",
    maxHeight: "400px",
    backgroundColor: "#fff",
    border: "2px solid #579862",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    marginTop: "4px",
    overflow: "hidden",
  },
  searchWrapper: {
    padding: "10px",
    borderBottom: "1px solid #eee",
  },
  list: {
    maxHeight: "300px",
    overflowY: "auto",
  },
  devisVierge: {
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    borderBottom: "1px solid #eee",
    color: "#333",
    ":hover": {
      backgroundColor: "#f0f9f1",
    },
  },
  devisViergeSelected: {
    backgroundColor: "#e8f5e9",
  },
  loading: {
    padding: "16px",
    textAlign: "center",
    color: "#999",
    fontSize: "14px",
  },
  empty: {
    padding: "16px",
    textAlign: "center",
    color: "#999",
    fontSize: "14px",
    fontStyle: "italic",
  },
};

export default GabaritSelector;
